import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, TextField, Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import { warehouseService } from '../services/api';

const SELECT_MENU_PROPS = {
  PaperProps: { sx: { maxHeight: 'min(40vh, 320px)' } },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

const FALLBACK_WAREHOUSES = [
  { id: 'production', name: 'Склад виробництва' },
  { id: 'raw', name: 'Склад сировини' },
  { id: 'dyes', name: 'Склад барвників' },
  { id: 'packaging', name: 'Склад упаковки' },
  { id: 'technical', name: 'Склад техматеріалів' },
  { id: 'semi', name: 'Склад напівфабрикатів' },
  { id: 'packaging_work', name: 'Склад для пакування' },
  { id: 'finished', name: 'Склад готової продукції' },
  { id: 'crushing_waste', name: 'Склад перемолів' },
  { id: 'blocking', name: 'Склад блокування' },
  { id: 'consumable', name: 'Склад розхідних матеріалів' },
  { id: 'defect', name: 'Склад браку' },
  { id: 'samples', name: 'Склад взірців' },
];

/**
 * Спільна форма ручного переміщення між складами.
 *
 * @param {object} props
 * @param {boolean} props.hubOpen
 * @param {function} props.onHubClose
 * @param {string} props.sourceWarehouseId — id поточного складу (виключається зі списку)
 * @param {string} [props.hubHint]
 * @param {string} [props.formTitle]
 * @param {Array} props.options — позиції з залишком
 * @param {function} props.getOptionLabel
 * @param {function} props.getOptionKey — унікальний ключ рядка
 * @param {function} props.getOptionQty — доступна кількість (число)
 * @param {string} [props.qtyLabel]
 * @param {boolean} [props.skipHub] — одразу форма (без проміжного вікна «Переміщення»)
 * @param {function} props.onSubmit — async ({ option, quantity, destination, note }) => void
 */
export default function ManualWarehouseTransferDialog({
  hubOpen,
  onHubClose,
  sourceWarehouseId,
  hubHint = 'Оберіть позицію, кількість і склад призначення. Рух зʼявиться в журналі; залишки оновляться одразу.',
  formTitle = 'Ручне переміщення',
  options = [],
  getOptionLabel = (o) => String(o?.id ?? ''),
  getOptionKey = (o) => String(o?.id ?? ''),
  getOptionQty = (o) => Number(o?.quantity) || 0,
  qtyLabel = 'Кількість, шт',
  skipHub = false,
  onSubmit,
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [destOptions, setDestOptions] = useState(
    () => FALLBACK_WAREHOUSES.filter((w) => w.id !== sourceWarehouseId),
  );
  const [selectedKey, setSelectedKey] = useState('');
  const [qty, setQty] = useState('');
  const [dest, setDest] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    warehouseService.getAll(sourceWarehouseId ? { exclude: sourceWarehouseId } : {})
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        if (list.length) {
          setDestOptions(list.filter((w) => w.id !== sourceWarehouseId));
        }
      })
      .catch(() => {
        setDestOptions(FALLBACK_WAREHOUSES.filter((w) => w.id !== sourceWarehouseId));
      });
  }, [sourceWarehouseId]);

  const materialOptions = useMemo(
    () => (options || []).filter((o) => getOptionQty(o) > 0),
    [options, getOptionQty],
  );

  const selected = useMemo(
    () => materialOptions.find((o) => getOptionKey(o) === selectedKey) || null,
    [materialOptions, selectedKey, getOptionKey],
  );

  const openForm = () => {
    setError('');
    setQty('');
    setNote('');
    setDest(destOptions[0]?.id || '');
    setSelectedKey(materialOptions[0] ? getOptionKey(materialOptions[0]) : '');
    setFormOpen(true);
  };

  // skipHub: кнопка «Переміщення» одразу відкриває форму з кількістю/складами
  useEffect(() => {
    if (hubOpen && skipHub) {
      openForm();
    }
    if (!hubOpen) {
      setFormOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openForm залежить від options/dest; достатньо hubOpen/skipHub
  }, [hubOpen, skipHub]);

  const handleSubmit = async () => {
    if (!selected) {
      setError('Оберіть позицію');
      return;
    }
    const q = parseFloat(qty);
    if (!Number.isFinite(q) || q <= 0) {
      setError('Вкажіть коректну кількість');
      return;
    }
    const avail = getOptionQty(selected);
    if (q > avail + 1e-9) {
      setError(`Доступно лише ${avail}`);
      return;
    }
    if (!dest) {
      setError('Оберіть склад призначення');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSubmit({
        option: selected,
        quantity: q,
        destination: dest,
        note,
      });
      setFormOpen(false);
      onHubClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || e.message || 'Помилка переміщення');
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    if (loading) return;
    setFormOpen(false);
    if (skipHub) onHubClose?.();
  };

  return (
    <>
      {!skipHub && (
        <Dialog open={hubOpen} onClose={onHubClose} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pr: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
              Переміщення
              <Button variant="contained" color="warning" startIcon={<SwapHorizIcon />} onClick={openForm}>
                Ручне переміщення
              </Button>
            </Box>
            <IconButton edge="end" onClick={onHubClose} aria-label="Закрити" size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              {hubHint}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onHubClose}>Закрити</Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>{formTitle}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {error && <Typography color="error">{error}</Typography>}
          <Autocomplete
            options={materialOptions}
            getOptionLabel={getOptionLabel}
            value={selected}
            onChange={(_, val) => setSelectedKey(val ? getOptionKey(val) : '')}
            renderInput={(params) => (
              <TextField {...params} label="Позиція (з поточного складу)" fullWidth />
            )}
            ListboxProps={{ style: { maxHeight: 'min(40vh, 320px)' } }}
            noOptionsText="Немає позицій з залишком"
          />
          <TextField
            label={qtyLabel}
            type="number"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            fullWidth
            inputProps={{ min: 0.001, step: 'any' }}
          />
          <TextField
            select
            label="Куди переміщуємо"
            value={destOptions.some((w) => w.id === dest) ? dest : (destOptions[0]?.id || '')}
            onChange={(e) => setDest(e.target.value)}
            fullWidth
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          >
            {destOptions.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Примітка"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={loading}>Скасувати</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!selectedKey || !qty || loading}
            onClick={handleSubmit}
          >
            {loading ? <CircularProgress size={20} /> : 'Перемістити'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
